// Organization Map — the org as a constellation.
//
// Eleven department nodes ring the Brain; selecting one fans out its agents;
// selecting an agent opens its card. It is the same data the list view renders
// — `normalizeAgents` from `orgModel`, nothing re-derived — laid out spatially
// so the shape of the organisation is visible at a glance rather than read.
//
// WHY NOT A FORCE LAYOUT
// ======================
// A physics simulation would put departments in a different place on every
// load, which makes the map unlearnable: the operator could never build muscle
// memory for where Compliance sits. Positions here are a deterministic ring,
// ordered by the department axis, so a department is always in the same place
// until the roster itself changes.
//
// Every label comes from the API's `department` field. No department name is
// written in this file — same rule the list view follows.
import React, { useMemo, useState } from "react";

import { normalizeAgents } from "./orgModel";
import "./org-constellation.css";

const CENTRE = { x: 50, y: 50 };
const RING = { x: 40.5, y: 39 };
/** Vertical gap between an agent's pill and the next, as a % of stage height.
 *  ~32px at the stage's usual height — one pill, so they cannot collide. */
const AGENT_GAP = 4.9;
const COLUMN_OFFSET = 13;

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

/** Title-case a snake_case department id for display. */
function labelOf(id) {
  return String(id || "")
    .split("_")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/** Group the roster into departments, ordered so positions stay stable. */
export function toDepartments(payload) {
  const agents = normalizeAgents(payload);
  const by = new Map();
  for (const a of agents) {
    const id = a.department || "unassigned";
    if (!by.has(id)) by.set(id, []);
    by.get(id).push(a);
  }
  const ids = [...by.keys()].sort();
  return ids.map((id, i) => {
    const members = by.get(id).slice().sort((x, y) => {
      // Heads first, then a stable alphabetical order.
      const hx = x.layer === "head" ? 0 : 1;
      const hy = y.layer === "head" ? 0 : 1;
      return hx - hy || String(x.title || x.agent_id).localeCompare(String(y.title || y.agent_id));
    });
    const angle = -Math.PI / 2 + (i * 2 * Math.PI) / ids.length;
    return {
      id,
      label: labelOf(id),
      members,
      total: members.length,
      live: members.filter((m) => m.lifecycle === "live").length,
      x: CENTRE.x + RING.x * Math.cos(angle),
      y: CENTRE.y + RING.y * Math.sin(angle),
      side: Math.cos(angle) < -0.15 ? "l" : Math.cos(angle) > 0.15 ? "r" : "c",
    };
  });
}


/**
 * Lay a department's agents out beside its node.
 *
 * A COLUMN, not an arc. Fanning angularly spreads agents across an arc whose
 * length does not grow with headcount, so a seven-agent department packed its
 * pills within a few percent of each other and they overlapped. A column with
 * a fixed minimum gap cannot collide however many agents a department gains.
 */
export function agentColumn(dept, count) {
  const right = dept.x > CENTRE.x;
  const x = clamp(dept.x + (right ? COLUMN_OFFSET : -COLUMN_OFFSET), 11, 86);
  const span = AGENT_GAP * (count - 1);
  // centred on the node where it fits, otherwise nudged to stay on the stage
  const top = clamp(dept.y - span / 2, 7, Math.max(7, 93 - span));
  return Array.from({ length: count }, (_, i) => ({
    x,
    y: top + i * AGENT_GAP,
    inward: right,          // the label grows toward the centre
  }));
}

export default function OrgConstellation({ payload, masterSrc }) {
  const departments = useMemo(() => toDepartments(payload), [payload]);
  const [deptId, setDeptId] = useState(null);
  const [agentId, setAgentId] = useState(null);

  const dept = departments.find((d) => d.id === deptId) || null;
  const agent = dept ? dept.members.find((m) => m.agent_id === agentId) || null : null;
  const positions = dept ? agentColumn(dept, dept.members.length) : [];

  const close = () => { setDeptId(null); setAgentId(null); };

  return (
    <div
      className={"oc-stage" + (dept ? " oc-has-detail" : "")}
      data-testid="oc-stage"
      onClick={close}
    >
      <div className="oc-orb" aria-hidden="true" />
      <div className="oc-wrap">
        {masterSrc ? (
          <div className="oc-core" aria-hidden="true"><img src={masterSrc} alt="" /></div>
        ) : null}

        <svg className="oc-wires" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
          {departments.map((d) => (
            <line key={d.id} x1={CENTRE.x} y1={CENTRE.y} x2={d.x} y2={d.y}
              stroke={d.id === deptId ? "rgba(80,230,255,.55)" : "rgba(80,230,255,.16)"}
              strokeWidth={d.id === deptId ? 0.34 : 0.18} vectorEffect="non-scaling-stroke" />
          ))}
          {dept && dept.members.map((m, i) => (
            <line key={m.agent_id} x1={dept.x} y1={dept.y} x2={positions[i].x} y2={positions[i].y}
              stroke={m.layer === "head" ? "rgba(255,79,200,.5)" : "rgba(80,230,255,.3)"}
              strokeWidth={0.16} vectorEffect="non-scaling-stroke" />
          ))}
        </svg>

        {departments.map((d, i) => (
          <button
            key={d.id}
            type="button"
            className={`oc-hex oc-${d.side}${d.id === deptId ? " oc-sel" : ""}${d.live === 0 ? " oc-dim" : ""}`}
            style={{ left: `${d.x}%`, top: `${d.y}%`, "--i": i }}
            data-testid={`oc-dept-${d.id}`}
            aria-expanded={d.id === deptId}
            aria-label={`${d.label}. ${d.live} of ${d.total} agents live. Open department.`}
            onClick={(e) => { e.stopPropagation(); setAgentId(null); setDeptId(d.id === deptId ? null : d.id); }}
          >
            <span className="oc-hexshape"><b>{d.total}</b></span>
            <span className="oc-tag">{d.label}</span>
          </button>
        ))}

        {dept && dept.members.map((m, i) => (
          <button
            key={m.agent_id}
            type="button"
            className={"oc-leaf"
              + (m.layer === "head" ? " oc-head" : "")
              + (m.lifecycle === "live" ? "" : " oc-dim")
              + (positions[i].inward ? " oc-inward" : "")
              + (m.agent_id === agentId ? " oc-sel" : "")}
            style={{ left: `${positions[i].x}%`, top: `${positions[i].y}%`, "--i": i }}
            data-testid={`oc-agent-${m.agent_id}`}
            aria-label={`${m.title || m.agent_id}. ${m.lifecycle}. Open agent card.`}
            onClick={(e) => { e.stopPropagation(); setAgentId(m.agent_id); }}
          >
            <span className="oc-bead" />
            <span className="oc-name">{m.title || m.agent_id}</span>
          </button>
        ))}
      </div>

      {dept ? (
        <div
          className={"oc-detail" + (dept.x > 50 ? " oc-onleft" : "")}
          role="dialog"
          aria-label={`${agent ? agent.title || agent.agent_id : dept.label} detail`}
          data-testid="oc-detail"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="oc-dhead">
            <div>
              <h3>{agent ? agent.title || agent.agent_id : dept.label}</h3>
              <div className={"oc-kind" + ((agent ? agent.lifecycle === "live" : dept.live > 0) ? "" : " oc-dm")}>
                {agent ? (agent.layer === "head" ? "DEPARTMENT HEAD" : String(agent.layer || "AGENT").toUpperCase())
                       : "DEPARTMENT"}
                {" · "}
                {agent ? String(agent.lifecycle || "unknown").toUpperCase() : `${dept.live} LIVE`}
              </div>
            </div>
            <button type="button" className="oc-close" onClick={close} aria-label="Close detail">×</button>
          </div>

          <dl className="oc-rows">
            {agent ? (
              <>
                <div><dt>Department</dt><dd>{dept.label}</dd></div>
                <div><dt>Layer</dt><dd>{agent.layer || "—"}</dd></div>
                <div><dt>Lifecycle</dt><dd>{agent.lifecycle || "—"}</dd></div>
                <div><dt>Agent id</dt><dd>{agent.agent_id}</dd></div>
              </>
            ) : (
              <>
                <div><dt>Agents</dt><dd>{dept.total}</dd></div>
                <div><dt>Live</dt><dd>{dept.live}</dd></div>
                <div><dt>Dormant</dt><dd>{dept.members.filter((m) => m.lifecycle === "dormant").length}</dd></div>
                <div><dt>Suspended</dt><dd>{dept.members.filter((m) => m.lifecycle === "suspended").length}</dd></div>
              </>
            )}
          </dl>
          <p className="oc-note">
            Lifecycle is not readiness. A dormant agent holds no grants and accumulates
            no authority while it waits.
          </p>
        </div>
      ) : null}
    </div>
  );
}
